import {sha256} from 'js-sha256';
import {withRequestTimeout} from '../components/chat/refreshQueue';
import {sessionFetch} from './authService';

/**
 * @typedef {Object} ChatSnapshot
 * @property {number} snapshot_event_id Committed watermark from the count's DB snapshot.
 * @property {string} computed_at UTC snapshot time.
 * @property {number} [unread_count]
 * @property {boolean} [has_unread]
 * @property {number} [conversation_snapshot_event_id] Bootstrap feed watermark, independent of unread and sync cursors.
 * @property {string} [conversation_computed_at]
 *
 * @typedef {Object} ConfirmedReadState
 * @property {number} event_id Committed read barrier; never an optimistic message sequence.
 * @property {number} [last_read_root_sequence]
 * @property {number} [last_read_reply_sequence]
 * @property {boolean} [following]
 */
const CHAT_BASE = '/goapi/chat';
const HASH_CHUNK_BYTES = 1024 * 1024;

export const ensureJobWorkerChannel = (jobId, userId, signal) => chatRequest(`/job-channels/${encodeURIComponent(jobId)}/worker`, {method: 'PUT', body: {user_id: userId}, signal});

export class ChatApiError extends Error {
    constructor(message, {status = 0, code = 'REQUEST_FAILED', requestId = null, retryAfterMs = 0} = {}) {
        super(message);
        this.name = 'ChatApiError';
        this.status = status;
        this.code = code;
        this.requestId = requestId;
        this.retryAfterMs = retryAfterMs;
    }
}
function chatHeaders(hasBody = false) {
    const token = localStorage.getItem('cosToken');
    const headers = new Headers();
    if (hasBody) headers.set('Content-Type', 'application/json');
    if (token) {
        headers.set('Authorization', /^Bearer\s/i.test(token) ? token : `Bearer ${token}`);
    }
    return headers;
}

export function chatRequest(path, options = {}) {
    if (/\/(attachments|translations|translate|language)(\/|$)/.test(path)) return performChatRequest(path, options);
    return withRequestTimeout(signal => performChatRequest(path, {...options, signal}), options.signal);
}

async function performChatRequest(path, {method = 'GET', body, signal} = {}) {
    const headers = chatHeaders(body !== undefined);
    const response = await sessionFetch(`${CHAT_BASE}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
        redirect: 'follow',
    });
    if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const details = payload?.error || {};
        throw new ChatApiError(details.message || `Chat request failed (${response.status})`, {
            status: response.status,
            code: details.code,
            requestId: details.request_id || response.headers.get('X-Request-ID'),
            retryAfterMs: retryAfterMilliseconds(response.headers.get('Retry-After')),
        });
    }
    if (response.status === 204) return null;
    return response.json();
}

async function signedBlobRequest(url, signal) {
    const response = await fetch(url, {signal});
    if (!response.ok) {
        throw new ChatApiError(`Attachment download failed (${response.status})`, {
            status: response.status,
            code: 'ATTACHMENT_DOWNLOAD_FAILED',
        });
    }
    return response.blob();
}

export function listChatUsers(signal, scopeOrOptions) {
    const options = typeof scopeOrOptions === 'string'
        ? {scope: scopeOrOptions}
        : (scopeOrOptions || {});
    const params = new URLSearchParams();
    if (options.scope) params.set('scope', options.scope);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.query?.trim()) params.set('q', options.query.trim());
    if (options.cursor) params.set('cursor', options.cursor);
    const query = params.toString();
    return chatRequest(`/users${query ? `?${query}` : ''}`, {signal});
}

export async function listAllConversations(signal) {
    const conversations = [];
    let afterId = 0;
    do {
        const params = new URLSearchParams({member_only: 'true', summary: 'true', limit: '100'});
        if (afterId > 0) params.set('after_id', String(afterId));
        const page = await chatRequest(`/conversations?${params}`, {signal});
        const batch = page?.conversations || [];
        conversations.push(...batch);
        if (batch.length < 100) break;
        afterId = batch[batch.length - 1].id;
    } while (true);
    return conversations;
}

export function getChatBootstrap({selectedId, signal} = {}) {
    const params = new URLSearchParams({limit: '50'});
    if (selectedId) params.set('selected_id', String(selectedId));
    return chatRequest(`/bootstrap?${params}`, {signal});
}

export function listConversationFeed({cursor, agentKey, query, signal} = {}) {
    const params = new URLSearchParams({sort: 'activity', summary: 'true', limit: '50'});
    if (cursor) params.set('cursor', cursor);
    if (agentKey) params.set('agent_key', agentKey);
    if (query) params.set('q', query);
    return chatRequest(`/conversations?${params}`, {signal});
}

export function listConversationGroups(signal) {
    return chatRequest('/conversation-groups?type=agent', {signal});
}

export function getUnreadStatus(signal) {
    return chatRequest('/unread-status', {signal});
}

export function queryConversationSummaries(conversationIds, signal) {
    return chatRequest('/conversation-summaries/query', {
        method: 'POST', body: {conversation_ids: conversationIds}, signal,
    });
}

export function createConversation(payload) {
    return chatRequest('/conversations', {method: 'POST', body: payload});
}

export function ensurePropertyChannel(propertyId, cosJobId, signal) {
    return chatRequest(`/property-channels/${encodeURIComponent(propertyId)}`, {
        method: 'PUT',
        body: {cos_job_id: cosJobId},
        signal,
    });
}

export function getConversation(conversationId, signal) {
    return withRequestTimeout(requestSignal => chatRequest(`/conversations/${encodeURIComponent(conversationId)}`, {signal: requestSignal}), signal);
}

export function listMessages(conversationId, {beforeSequence, afterSequence, signal} = {}) {
    const params = new URLSearchParams({limit: '50'});
    if (beforeSequence) params.set('before_sequence', String(beforeSequence));
    if (afterSequence) params.set('after_sequence', String(afterSequence));
    return withRequestTimeout(requestSignal => chatRequest(`/conversations/${conversationId}/messages?${params}`, {signal: requestSignal}), signal);
}

export function listReplies(conversationId, rootId, {beforeSequence, afterSequence, signal} = {}) {
    const params = new URLSearchParams({limit: '50'});
    if (beforeSequence) params.set('before_sequence', String(beforeSequence));
    if (afterSequence) params.set('after_sequence', String(afterSequence));
    return withRequestTimeout(requestSignal => chatRequest(`/conversations/${conversationId}/messages/${rootId}/replies?${params}`, {signal: requestSignal}), signal);
}

export function createMessage(conversationId, body, clientMessageId, attachmentIds = [], quotedMessageId = null, originalBody) {
    return chatRequest(`/conversations/${conversationId}/messages`, {
        method: 'POST', body: {
            client_message_id: clientMessageId,
            body: body || null,
            attachment_ids: attachmentIds,
            quoted_message_id: quotedMessageId || null, original_body: originalBody,
        },
    });
}

export function createReply(conversationId, rootId, body, clientMessageId, attachmentIds = [], quotedMessageId = null, originalBody) {
    return chatRequest(`/conversations/${conversationId}/messages/${rootId}/replies`, {
        method: 'POST', body: {
            client_message_id: clientMessageId,
            body: body || null,
            attachment_ids: attachmentIds,
            quoted_message_id: quotedMessageId || null, original_body: originalBody,
        },
    });
}

export function forwardMessage(messageId, targets) {
    return chatRequest(`/messages/${messageId}/forwards`, {method: 'POST', body: {targets}});
}

export function getForwardTargets(messageId, signal) {
    return chatRequest(`/messages/${encodeURIComponent(messageId)}/forward-targets`, {signal});
}

export const getMessage = (messageId, signal) => chatRequest(`/messages/${encodeURIComponent(messageId)}`, {signal});

export function getMessageQuotePreview(messageId, signal) {
    return chatRequest(`/messages/${encodeURIComponent(messageId)}/quote-preview`, {signal});
}

export function createAttachmentUpload(payload, signal) {
    return chatRequest('/attachments/uploads', {method: 'POST', body: payload, signal});
}

export async function completeAttachmentUpload(attachmentId, signal) {
    while (true) {
        try {
            return await chatRequest(`/attachments/${attachmentId}/complete`, {method: 'POST', body: {}, signal});
        } catch (error) {
            if (!isRetryableAttachmentCompletionError(error)) throw error;
            await abortableDelay(error.retryAfterMs || 2000, signal);
        }
    }
}

export function isRetryableAttachmentCompletionError(error) {
    return (error?.status === 429 && String(error?.code || '').startsWith('ATTACHMENT_VERIFICATION_'))
        || (error?.status === 503 && error?.code === 'ATTACHMENT_VERIFICATION_UNAVAILABLE')
        || error instanceof TypeError;
}

function retryAfterMilliseconds(value) {
    const seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) return 0;
    return Math.min(30000, Math.max(250, Math.round(seconds * 1000)));
}

function abortableDelay(milliseconds, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(attachmentAbortError());
            return;
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', abort);
            resolve();
        }, milliseconds);
        const abort = () => {
            clearTimeout(timer);
            reject(attachmentAbortError());
        };
        signal?.addEventListener('abort', abort, {once: true});
    });
}

function attachmentAbortError() {
    return new DOMException('Attachment upload cancelled', 'AbortError');
}

function throwIfAttachmentAborted(signal) {
    if (signal?.aborted) throw attachmentAbortError();
}

function yieldToBrowser() {
    return new Promise(resolve => setTimeout(resolve, 0));
}

// Web Crypto requires one contiguous ArrayBuffer. Incremental hashing keeps
// peak input memory bounded to one chunk and makes file preparation cancellable.
export async function sha256Hex(file, {signal, onProgress} = {}) {
    const hasher = sha256.create();
    const total = Math.max(0, file.size || 0);
    for (let start = 0; start < total; start += HASH_CHUNK_BYTES) {
        throwIfAttachmentAborted(signal);
        const end = Math.min(start + HASH_CHUNK_BYTES, total);
        const bytes = await file.slice(start, end).arrayBuffer();
        throwIfAttachmentAborted(signal);
        hasher.update(new Uint8Array(bytes));
        onProgress?.(Math.round((end / total) * 100));
        if (end < total) await yieldToBrowser();
    }
    throwIfAttachmentAborted(signal);
    return hasher.hex();
}

export function uploadAttachmentObject(uploadUrl, headers, file, {signal, onProgress} = {}) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        const abort = () => request.abort();
        const cleanUp = () => signal?.removeEventListener('abort', abort);
        request.open('PUT', uploadUrl);
        Object.entries(headers || {}).forEach(([name, value]) => {
            // Browsers set Content-Length themselves and disallow scripts from setting it.
            if (name.toLowerCase() !== 'content-length') request.setRequestHeader(name, value);
        });
        request.upload.onprogress = event => {
            if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
        };
        request.onload = () => {
            cleanUp();
            if (request.status >= 200 && request.status < 300) {
                onProgress?.(100);
                resolve();
                return;
            }
            reject(new ChatApiError(`Attachment upload failed (${request.status})`, {
                status: request.status,
                code: 'ATTACHMENT_UPLOAD_FAILED',
            }));
        };
        request.onerror = () => {
            cleanUp();
            reject(new ChatApiError('Attachment upload failed', {code: 'ATTACHMENT_UPLOAD_FAILED'}));
        };
        request.onabort = () => {
            cleanUp();
            reject(new DOMException('Attachment upload cancelled', 'AbortError'));
        };
        if (signal?.aborted) {
            reject(new DOMException('Attachment upload cancelled', 'AbortError'));
            return;
        }
        signal?.addEventListener('abort', abort, {once: true});
        request.send(file);
    });
}

export async function uploadChatAttachment(conversationId, file, {contentType = file.type, signal, onProgress} = {}) {
    onProgress?.({stage: 'hashing', progress: 0});
    const checksum = await sha256Hex(file, {
        signal,
        onProgress: progress => onProgress?.({stage: 'hashing', progress}),
    });
    const allocation = await createAttachmentUpload({
        conversation_id: conversationId,
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
        sha256: checksum,
    }, signal);
    onProgress?.({stage: 'uploading', progress: 0});
    await uploadAttachmentObject(allocation.upload_url, allocation.headers, file, {
        signal,
        onProgress: progress => onProgress?.({stage: 'uploading', progress}),
    });
    onProgress?.({stage: 'verifying', progress: 100});
    const completed = await completeAttachmentUpload(allocation.attachment_id, signal);
    return {
        id: allocation.attachment_id,
        original_filename: file.name,
        content_type: completed.content_type || contentType,
        size_bytes: file.size,
        download_path: `/goapi/chat/attachments/${allocation.attachment_id}/download`,
        local_file: file,
    };
}

export function createAttachmentDownload(attachment, signal) {
    const path = attachment.download_path || `/goapi/chat/attachments/${attachment.id}/download`;
    const relativePath = path.startsWith(CHAT_BASE) ? path.slice(CHAT_BASE.length) : path;
    const separator = relativePath.includes('?') ? '&' : '?';
    return chatRequest(`${relativePath}${separator}format=json`, {signal});
}

export async function loadChatAttachmentBlob(attachment, signal) {
    const allocation = await createAttachmentDownload(attachment, signal);
    return signedBlobRequest(allocation.download_url, signal);
}

export async function downloadChatAttachment(attachment) {
    let objectUrl;
    const href = attachment.local_file
        ? (objectUrl = URL.createObjectURL(attachment.local_file))
        : (await createAttachmentDownload(attachment)).download_url;
    const link = document.createElement('a');
    link.href = href;
    link.download = attachment.original_filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export async function openChatAttachment(attachment) {
    let objectUrl;
    const href = attachment.local_file
        ? (objectUrl = URL.createObjectURL(attachment.local_file))
        : (await createAttachmentDownload(attachment)).download_url;
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export function updateMessage(messageId, body, version) {
    return chatRequest(`/messages/${messageId}`, {method: 'PATCH', body: {body, version}});
}

export function deleteMessage(messageId) {
    return chatRequest(`/messages/${messageId}`, {method: 'DELETE'});
}

export function addReaction(messageId, emoji) {
    return chatRequest(`/messages/${messageId}/reactions`, {method: 'PUT', body: {emoji}});
}

export function removeReaction(messageId, emoji) {
    return chatRequest(`/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`, {method: 'DELETE'});
}

export function updateReadCursor(conversationId, throughSequence, signal) {
    return withRequestTimeout(requestSignal => chatRequest(`/conversations/${conversationId}/read?return_state=true`, {
        method: 'PUT', body: {through_sequence: throughSequence}, signal: requestSignal,
    }), signal);
}

export function updateThreadState(rootId, payload, signal) {
    return withRequestTimeout(requestSignal => chatRequest(`/messages/${rootId}/thread-state?return_state=true`, {method: 'PUT', body: payload, signal: requestSignal}), signal);
}

export function createWebSocketTicket(deviceId, signal) {
    return chatRequest('/ws-ticket', {method: 'POST', body: {device_id: deviceId}, signal});
}

export function syncEvents(afterEventId, throughEventId, signal) {
    return chatRequest(`/sync?after_event_id=${afterEventId}&through_event_id=${throughEventId}`, {signal});
}

export function getPushConfig(signal) {
    return chatRequest('/push-config', {signal});
}

export function registerPushToken(deviceId, payload, signal) {
    return chatRequest(`/devices/${encodeURIComponent(deviceId)}/push-token`, {
        method: 'PUT', body: payload, signal,
    });
}

export function deletePushToken(deviceId, signal) {
    return chatRequest(`/devices/${encodeURIComponent(deviceId)}/push-token`, {
        method: 'DELETE', signal,
    });
}

export function chatWebSocketUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${CHAT_BASE}/ws`;
}
